import { A, G } from '@mobily/ts-belt';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { GetB2bContractsResponse } from '@Common/types/bo/b2b/contracts';
import { GetB2bCompaniesPlansResponse } from '@Common/types/bo/b2b/plans';
import {
  isERContract,
  isInvalidUnusedVacationDaysAllocationComputedBy,
  isInvalidVoluntaryDepositContract,
  validateCampaignsProcessingOptionsOfContracts,
} from '@Common/validators/contracts';

import { isCampaignVacationDaysAllocation } from '@sh4res/bo-common/components/B2bCampaigns/helpers/isCampaignVacationDaysAllocation';
import {
  AllocationRecord,
  isAllocationAccepted,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/AllocationsConfirmationStep';
import {
  getMatchingContributionCeilingOverruns,
  MATCHING_CONTRIBUTION_CEILING_OVERRUNS_FIELD,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/matchingContributionCeilings';
import {
  isZeroAmount,
  type VacationDaysAllocationFormRow,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/vacationDaysAllocationRows';
import { ExtraValidationResult, ValidateFunc } from '@sh4res/bo-common/components/StepperForm';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

export const validateCampaignAllocations: ValidateFunc = async (
  form,
  dataProvider,
  resourceBasePath,
) => {
  const { allocations = [], amount_input, type } = form.getValues();
  const allocationsWithoutEmpty = (allocations as AllocationRecord[]).filter(
    (alloc): alloc is AllocationRecord & { amount: number } =>
      G.isNotNullable(alloc.amount) || type === 'matching_contribution',
  );

  if (allocationsWithoutEmpty.length === 0) {
    return {
      isValid: false,
      message: 'resources.bo_common.b2b.campaigns.sections.review.errors.no_allocations',
    };
  }

  const allocatedEmployeeIds = allocationsWithoutEmpty
    .map((alloc) => alloc.employee_id)
    .filter((employeeId) => G.isNotNullable(employeeId));
  if (new Set(allocatedEmployeeIds).size !== allocatedEmployeeIds.length) {
    return {
      isValid: false,
      message: 'resources.bo_common.b2b.campaigns.sections.review.errors.duplicate_employee',
    };
  }

  if (
    allocationsWithoutEmpty
      .filter(
        (alloc) =>
          !alloc.is_campaigns_blocked &&
          isAllocationAccepted({
            campaignType: type,
            amount: alloc.amount,
            companyCollectedChoices: alloc.company_collected_choices,
          }),
      )
      .some(
        (alloc) =>
          !alloc.employee_exists ||
          !alloc.employee_fully_onboarded ||
          alloc.is_user_closed_or_archived,
      )
  ) {
    return {
      isValid: false,
      message:
        'resources.bo_common.b2b.campaigns.sections.review.errors.invalid_employee_allocations',
    };
  }

  const total = allocationsWithoutEmpty.reduce((acc, alloc) => acc + alloc.amount, 0);
  if (type !== 'matching_contribution' && G.isNotNullable(amount_input) && total !== amount_input) {
    return {
      isValid: false,
      message: 'resources.bo_common.b2b.campaigns.sections.review.errors.total_not_match',
    };
  }

  const totalMc = allocationsWithoutEmpty.reduce(
    (acc, alloc) =>
      acc +
      (alloc.company_collected_choices?.reduce(
        (choiceAcc, choice) => choiceAcc + (choice.matching_contribution_amount ?? 0),
        0,
      ) ?? 0),
    0,
  );
  if (
    type === 'matching_contribution' &&
    G.isNotNullable(amount_input) &&
    totalMc !== amount_input
  ) {
    return {
      isValid: false,
      message: 'resources.bo_common.b2b.campaigns.sections.review.errors.total_not_match_mc',
    };
  }

  if (
    type !== 'matching_contribution' &&
    allocationsWithoutEmpty.some(
      (alloc) =>
        G.isNotNullable(alloc.company_collected_choices) &&
        alloc.company_collected_choices.some((choice) => choice.amount === 0),
    )
  ) {
    return {
      isValid: false,
      message: 'resources.bo_common.b2b.campaigns.sections.review.errors.some_choice_at_zero',
    };
  }

  if (
    type === 'matching_contribution' &&
    allocationsWithoutEmpty.some(
      (alloc) =>
        G.isNotNullable(alloc.company_collected_choices) &&
        alloc.company_collected_choices.some((choice) => choice.matching_contribution_amount === 0),
    )
  ) {
    return {
      isValid: false,
      message: 'resources.bo_common.b2b.campaigns.sections.review.errors.some_choice_at_zero_mc',
    };
  }

  const ceilingOverruns = await getMatchingContributionCeilingOverruns(
    form,
    dataProvider,
    resourceBasePath,
  );
  form.setValue(MATCHING_CONTRIBUTION_CEILING_OVERRUNS_FIELD, ceilingOverruns);

  if (ceilingOverruns.length > 0) {
    return {
      isValid: false,
      message:
        'resources.bo_common.b2b.campaigns.sections.review.errors.matching_contribution_ceiling_exceeded',
    };
  }

  return { isValid: true };
};

/**
 * Employees only answer during a consultation period, so a distributor-collected campaign cannot be
 * published without one. The dates live on whichever step owns them for the selected processing
 * options, and switching choice_collected_by over to the distributor can leave them unfilled.
 */
const validateConsultationPeriod = (form: UseFormReturn<FieldValues>): ExtraValidationResult => {
  const {
    choice_collected_by: choiceCollectedBy,
    start_date: startDate,
    end_date: endDate,
  } = form.getValues();

  if (
    choiceCollectedBy === 'distributor' &&
    (G.isNullable(startDate) || G.isNullable(endDate) || startDate === '' || endDate === '')
  ) {
    return {
      isValid: false,
      message:
        'resources.bo_common.b2b.campaigns.sections.allocations.errors.missing_consultation_period',
    };
  }

  return { isValid: true };
};

export const validateCampaignConfirmationStep =
  (isOngoingConsultationEdit: boolean): ValidateFunc =>
  async (form, dataProvider, resourceBasePath) => {
    const consultationPeriod = validateConsultationPeriod(form);
    if (!consultationPeriod.isValid) {
      return consultationPeriod;
    }

    return isOngoingConsultationEdit
      ? validateCampaignAllocations(form, dataProvider, resourceBasePath)
      : { isValid: true };
  };

// Every allocation and every day type allocation must have an amount, including those not
// displayed in the current grid page
export const validateVacationDaysAllocations: ValidateFunc = async (
  form,
  dataProvider,
  resourceBasePath,
) => {
  const { allocations = [] } = form.getValues();
  if ((allocations as VacationDaysAllocationFormRow[]).some((alloc) => alloc.day_types_mismatch)) {
    return {
      isValid: false,
      message: 'resources.bo_common.b2b.campaigns.sections.review.errors.day_types_mismatch',
    };
  }
  if (
    (allocations as VacationDaysAllocationFormRow[]).some(
      (alloc) =>
        isZeroAmount(alloc.amount) ||
        alloc.day_type_allocations?.some((entry) => isZeroAmount(entry.amount)),
    )
  ) {
    return {
      isValid: false,
      message: 'resources.bo_common.b2b.campaigns.sections.review.errors.zero_amount_allocations',
    };
  }

  return validateCampaignAllocations(form, dataProvider, resourceBasePath);
};

export const validateCampaignGeneralInfoStep: ValidateFunc = async (
  form,
  dataProvider,
  resourceBasePath,
) => {
  const { b2b_contract_ids = [], ...values } = form.getValues();

  const { data: contracts } = await dataProvider.getList<GetB2bContractsResponse[number]>(
    `${resourceBasePath}contracts`,
    {
      filter: { id: b2b_contract_ids },
      sort: { field: 'created_at', order: 'DESC' },
      pagination: { page: 1, perPage: 100 },
    },
  );

  const contractsFiltered = contracts.every((contract) => isERContract(contract.contract_type))
    ? contracts
    : contracts.filter((contract) => !isERContract(contract.contract_type));

  const campaignType = form.getValues('type');

  if (
    campaignType === 'voluntary_deposit' &&
    contractsFiltered.some(isInvalidVoluntaryDepositContract)
  ) {
    return {
      isValid: false,
      message:
        'resources.bo_common.b2b.campaigns.sections.general.errors.invalid_contracts_voluntary_deposit',
    };
  }

  if (
    campaignType === 'unused_vacation_days' &&
    contractsFiltered.some((contract) =>
      isInvalidUnusedVacationDaysAllocationComputedBy(
        contract.campaigns.unused_vacation_days?.allocation_computed_by,
      ),
    )
  ) {
    return {
      isValid: false,
      message:
        'resources.bo_common.b2b.campaigns.sections.general.errors.invalid_contracts_cet_allocation_computed_by',
    };
  }

  if (
    !validateCampaignsProcessingOptionsOfContracts(
      form.getValues('type'),
      contractsFiltered.map((contract) => contract.campaigns),
      {
        checkChoiceAndPayout: isBoExternal(),
        campaignAllocationComputedBy: form.getValues('allocation_computed_by'),
      },
    )
  ) {
    return {
      isValid: false,
      message:
        'resources.bo_common.b2b.campaigns.sections.review.errors.different_processing_options',
    };
  }

  const { data: plans } = await dataProvider.getList<GetB2bCompaniesPlansResponse[number]>(
    `${resourceBasePath}companies/plans`,
    {
      filter: { b2b_contract_id: b2b_contract_ids },
      sort: { field: 'created_at', order: 'DESC' },
      pagination: { page: 1, perPage: 100 },
    },
  );

  if (plans.length === 0) {
    return {
      isValid: false,
      message: 'resources.bo_common.b2b.campaigns.sections.review.errors.no_contract_plans',
    };
  }

  if (
    isCampaignVacationDaysAllocation(values) &&
    A.isEmpty(values.allocations ?? []) &&
    G.isNotNullable(values.allocations_file) // create flow
  ) {
    return {
      isValid: false,
      message:
        'resources.bo_common.b2b.campaigns.sections.review.errors.invalid_employee_allocations',
    };
  }

  // A CET campaign collected by the distributor is saved straight from this step, which is also
  // where its consultation period is captured.
  if (isCampaignVacationDaysAllocation(values)) {
    return validateConsultationPeriod(form);
  }

  return { isValid: true };
};
